import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from './contexts/ThemeContext';
import { useAuth } from './contexts/AuthContext';
import { Sidebar } from './components/shell/Sidebar';
import { Header } from './components/shell/Header';
import { LoginPage, RegisterPage } from './pages/auth/LoginPage';
import { MfaChallengePage } from './pages/auth/MfaChallengePage';
import { MfaBackupCodePage } from './pages/auth/MfaBackupCodePage';
import { MfaSetupPage } from './pages/auth/MfaSetupPage';
import { AdminDashboardPage } from './pages/dashboard/AdminDashboardPage';
import { AbteilungPage } from './pages/abteilung/AbteilungPage';
import { useMyDepartments, resetMyDepartmentsCache } from './lib/departments';
import { AnfragenPage } from './pages/anfragen/AnfragenPage';
import { BkAnfragenPage } from './pages/anfragen_bk/BkAnfragenPage';
import { BeendeteAnfragenPage } from './pages/anfragen_beendet/BeendeteAnfragenPage';
import { MatchingPage } from './pages/matching/MatchingPage';
import { EmailPage } from './pages/email/EmailPage';
import { KalenderPage } from './pages/kalender/KalenderPage';
import { FamilienkontoListPage } from './pages/pflege/familienkonto/FamilienkontoListPage';
import { FamilienkontoDetailPage } from './pages/pflege/familienkonto/FamilienkontoDetailPage';
import { LegacyPflegeRedirect } from './pages/pflege/familienkonto/LegacyPflegeRedirect';
import { BetreuungPage } from './pages/betreuung/BetreuungPage';
import { BkDetailPage } from './pages/betreuung/BkDetailPage';
import { ProfilPage } from './pages/profil/ProfilPage';
import { EinstellungenPage } from './pages/einstellungen/EinstellungenPage';
import { AdminEinstellungenPage } from './pages/einstellungen/AdminEinstellungenPage';
import { BenutzerPage } from './pages/benutzer/BenutzerPage';
import { ChatPage } from './pages/chat/ChatPage';
import { getThemeColors } from './lib/theme';
import { useRoute, parseFamilyRoute, type PageId } from './lib/route';
import { NavSlotsProvider, useNavSlotsValue } from './components/shell/ProfileNavContext';
import { ToastHost } from './lib/toast';
import { CallPopup } from './components/shell/CallPopup';
import { IncomingCallAlert } from './components/shell/IncomingCallAlert';
import { FeedbackWidget } from './components/shell/FeedbackWidget';

type Page = PageId;

// Auth views — extended with MFA screens
type AuthView =
  | 'login'
  | 'register'
  | 'mfa_challenge'
  | 'mfa_backup'
  | 'mfa_setup'
  | 'app';

// Titel/Subtitel je Seite als i18n-Key-Paare — übersetzt wird erst beim Rendern
// in AppInner (siehe `tr(titleKey)`/`tr(subtitleKey)`), da Hooks hier auf
// Modulebene nicht verfügbar sind.
const PAGE_TITLES: Record<Page, { titleKey: string; subtitleKey: string }> = {
  dashboard:     { titleKey: 'nav.menu.dashboard',           subtitleKey: 'nav.subtitle.dashboard'        },
  anfragen:      { titleKey: 'nav.menu.anfragen',            subtitleKey: 'nav.subtitle.anfragen'         },
  anfragen_bk:   { titleKey: 'nav.menu.anfragen_bk',         subtitleKey: 'nav.subtitle.anfragen_bk'      },
  anfragen_beendet:    { titleKey: 'nav.menu.anfragen_beendet',    subtitleKey: 'nav.subtitle.anfragen_beendet' },
  anfragen_bk_beendet: { titleKey: 'nav.menu.anfragen_bk_beendet', subtitleKey: 'nav.subtitle.anfragen_bk_beendet' },
  matching:      { titleKey: 'nav.menu.matching',            subtitleKey: 'nav.subtitle.matching'         },
  chat:          { titleKey: 'nav.menu.chat',                subtitleKey: 'nav.subtitle.chat'             },
  email:         { titleKey: 'nav.menu.email',               subtitleKey: 'nav.subtitle.email'            },
  kalender:      { titleKey: 'nav.menu.kalender',            subtitleKey: 'nav.subtitle.kalender'         },
  pflege:        { titleKey: 'nav.menu.pflege',              subtitleKey: 'nav.subtitle.pflege'           },
  betreuung:     { titleKey: 'nav.menu.betreuung',           subtitleKey: 'nav.subtitle.betreuung'        },
  benutzer:      { titleKey: 'nav.account.benutzer',         subtitleKey: 'nav.subtitle.benutzer'         },
  rollen:        { titleKey: 'nav.account.rollen',           subtitleKey: 'nav.subtitle.rollen'           },
  profil:        { titleKey: 'nav.account.profil',           subtitleKey: 'nav.subtitle.profil'           },
  einstellungen: { titleKey: 'nav.account.einstellungen',    subtitleKey: 'nav.subtitle.einstellungen'    },
  admin_einstellungen: { titleKey: 'nav.account.admin_einstellungen', subtitleKey: 'nav.subtitle.admin_einstellungen' },
  // Titel/Untertitel der Abteilungsseite hängen von Tab und Abteilung ab (siehe AppInner).
  abteilung:     { titleKey: 'nav.menu.workflow',            subtitleKey: 'nav.subtitle.abteilung'        },
};

export function App() {
  return (
    <NavSlotsProvider>
      <AppInner />
    </NavSlotsProvider>
  );
}

function AppInner() {
  const { dark, setDark, density, setDensity, collapsed, setCollapsed } = useTheme();
  const { isAuthenticated, logout } = useAuth();
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);

  const [authView, setAuthView] = useState<AuthView>(() =>
    isAuthenticated ? 'app' : 'login'
  );
  const { page, sub, tab, extra, navigate } = useRoute();
  const { departments: myDepartments } = useMyDepartments(isAuthenticated && authView === 'app');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated && (authView === 'login' || authView === 'mfa_challenge' || authView === 'mfa_setup')) {
      setAuthView('app');
    }
  }, [isAuthenticated, authView]);

  const handleSetPage = (p: string) => {
    if (p in PAGE_TITLES) navigate(p as Page);
  };

  const handleLogout = async () => {
    await logout();
    // Menü des nächsten Admins darf nicht kurz die Abteilungen des vorigen zeigen.
    resetMyDepartmentsCache();
    setAuthView('login');
  };

  // ── Auth screens ──────────────────────────────────────────────────────────

  if (authView === 'login') {
    return (
      <LoginPage
        dark={dark}
        onRegister={() => setAuthView('register')}
        onMfaChallenge={() => setAuthView('mfa_challenge')}
        onMfaSetupRequired={() => setAuthView('mfa_setup')}
      />
    );
  }

  if (authView === 'register') {
    return (
      <RegisterPage
        dark={dark}
        onLogin={() => setAuthView('login')}
      />
    );
  }

  // MFA challenge: enter 6-digit code
  if (authView === 'mfa_challenge') {
    return (
      <MfaChallengePage
        dark={dark}
        onSuccess={() => setAuthView('app')}
        onUseBackup={() => setAuthView('mfa_backup')}
        onCancel={() => setAuthView('login')}
      />
    );
  }

  // MFA backup code recovery
  if (authView === 'mfa_backup') {
    return (
      <MfaBackupCodePage
        dark={dark}
        onSuccess={() => setAuthView('app')}
        onBack={() => setAuthView('mfa_challenge')}
        onCancel={() => setAuthView('login')}
      />
    );
  }

  // Force MFA setup wizard (mfa_setup_pending=true after login)
  if (authView === 'mfa_setup') {
    return (
      <MfaSetupPage
        dark={dark}
        onComplete={() => setAuthView('app')}
        onLogout={handleLogout}
      />
    );
  }

  // ── App shell ─────────────────────────────────────────────────────────────

  // Navbar-Slots: Detailseiten (Familienkonto/BK) heben ihre Kopfzeile hierher.
  const navSlots = useNavSlotsValue();
  // Ein Lead unter /pflege/{id} gehört noch zu „Anfragen" — die Detailseite
  // meldet das über den activeMenu-Slot (Route bleibt unverändert).
  const menuPage: Page = navSlots.activeMenu && navSlots.activeMenu in PAGE_TITLES
    ? navSlots.activeMenu as Page
    : page;
  const { titleKey, subtitleKey } = PAGE_TITLES[menuPage] ?? PAGE_TITLES.dashboard;
  let title = tr(titleKey);
  let subtitle = tr(subtitleKey);
  if (menuPage === 'abteilung') {
    // Kopf der Abteilungsseite: Titel = Tab (Dashboard / Workflow / Archiv),
    // Unterzeile = „Abteilung <Name>".
    const dept = myDepartments.find(d => d.slug === sub);
    const tabKey = tab === 'dashboard' ? 'nav.menu.dashboard' : tab === 'beendet' ? 'nav.menu.anfragen_beendet_sub' : 'nav.menu.workflow';
    title = tr(tabKey);
    subtitle = dept ? tr('nav.menu.department', { name: dept.name }) : tr('nav.subtitle.abteilung');
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: t.bg }}>
      <ToastHost dark={dark} />
      <CallPopup dark={dark} />
      <IncomingCallAlert dark={dark} />
      {/* feedbackhub-Widget — nur im angemeldeten Bereich, nicht auf /login */}
      <FeedbackWidget />
      <Sidebar
        page={menuPage}
        setPage={handleSetPage}
        dark={dark}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        routeSub={sub}
        routeTab={tab}
        onOpenDepartment={(slug, dTab) => navigate('abteilung', slug, dTab)}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        <Header
          pageTitle={title}
          subtitle={subtitle}
          dark={dark}
          setDark={setDark}
          setPage={handleSetPage}
          onLogout={handleLogout}
          onOpenMobileMenu={() => setMobileMenuOpen(true)}
          leadingSlot={navSlots.leading}
          trailingSlot={navSlots.trailing}
        />

        <main className="app-main" style={{
          flex: 1,
          overflow: page === 'chat' ? 'hidden' : 'auto',
          background: t.bg,
          display: page === 'chat' ? 'flex' : undefined,
          flexDirection: page === 'chat' ? 'column' : undefined,
        }}>
          {page === 'dashboard' && (
            <AdminDashboardPage
              dark={dark}
              density={density}
              setPage={handleSetPage}
            />
          )}
          {page === 'abteilung' && (
            <AbteilungPage
              slug={sub}
              tab={tab}
              dark={dark}
              density={density}
              setPage={handleSetPage}
              navigate={navigate}
            />
          )}
          {page === 'anfragen' && (
            <AnfragenPage
              dark={dark}
              density={density}
              // Öffnet die VOLLE PB-Detailansicht der Pflege-Seite
              // (/pflege/{userId}, users.id führt seit 23.09.2026 überall).
              onOpenPb={(userId) => navigate('pflege', String(userId))}
            />
          )}
          {page === 'anfragen_bk' && (
            <BkAnfragenPage dark={dark} density={density} />
          )}
          {page === 'anfragen_beendet' && (
            <BeendeteAnfragenPage scope="pb" dark={dark} density={density} />
          )}
          {page === 'anfragen_bk_beendet' && (
            <BeendeteAnfragenPage scope="bk" dark={dark} density={density} />
          )}
          {page === 'email' && (
            <EmailPage dark={dark} density={density} />
          )}
          {page === 'matching' && (
            <MatchingPage dark={dark} density={density} />
          )}
          {page === 'kalender' && (
            <KalenderPage dark={dark} density={density} />
          )}
          {page === 'pflege' && (() => {
            // /pflege bündelt Familienkonten. parseFamilyRoute liest die URL
            // semantisch aus — s. lib/route.ts für das vollständige Schema
            // (kanonisch /pflege/{userId}[/{tab}], Konto-only /pflege/konto/
            // {accountId}, Kompat /pflege/{accountId}/{profileId}[/{tab}]).
            const fr = parseFamilyRoute(sub, tab, extra);
            if (fr.isList) {
              return (
                <FamilienkontoListPage
                  dark={dark}
                  density={density}
                  onOpenAccount={(id) => navigate('pflege', 'konto', String(id))}
                  onNewAccount={() => navigate('pflege', 'new')}
                />
              );
            }
            if (fr.isLegacy && fr.accountId != null && fr.profileId != null) {
              return (
                <LegacyPflegeRedirect
                  accountId={fr.accountId}
                  profileId={fr.profileId}
                  tabSlug={fr.tabSlug}
                  dark={dark}
                  navigate={navigate}
                />
              );
            }
            // Kein `key` hier: ein Wechsel des aktiven Mitglieds ändert
            // `userId`, soll aber NICHT das Konto/die Mitgliederliste neu
            // laden (Flicker) — FamilienkontoDetailPage hält den Konto-
            // Zustand selbst stabil und resettet nur bei echtem Konto-
            // Wechsel intern (s. dortige Effects).
            return (
              <FamilienkontoDetailPage
                isNew={fr.isNew}
                accountId={fr.isAccountOnly ? fr.accountId : null}
                userId={fr.isAccountOnly || fr.isNew ? null : fr.userId}
                activeTab={fr.tabSlug}
                dark={dark}
                onBack={() => navigate('pflege', null)}
                onOpenAccount={(id) => navigate('pflege', 'konto', String(id))}
                onSelectMember={(uid, accId) =>
                  uid != null ? navigate('pflege', String(uid)) : navigate('pflege', 'konto', String(accId))
                }
                onSelectMemberTab={(uid, accId, _pid, pbTab) =>
                  uid != null ? navigate('pflege', String(uid), pbTab) : navigate('pflege', 'konto', String(accId))
                }
              />
            );
          })()}
          {page === 'chat' && (
            <ChatPage dark={dark} density={density} />
          )}
          {/* WP8: /betreuung ohne sub = Liste (unverändert); /betreuung/{id}[/{tab}]
              = neue BK-Detailseite. Einzige Änderung an der bestehenden Zeile:
              `!sub`-Guard, damit Liste und Detail sich nicht doppeln. */}
          {page === 'betreuung' && !sub && (
            <BetreuungPage dark={dark} density={density} />
          )}
          {page === 'betreuung' && sub != null && /^\d+$/.test(sub) && (
            <BkDetailPage
              userId={Number(sub)}
              tab={tab}
              dark={dark}
              onClose={() => navigate('betreuung', null)}
              onSelectTab={(bkTab) => navigate('betreuung', sub, bkTab)}
            />
          )}
          {page === 'benutzer' && (
            <BenutzerPage dark={dark} density={density} />
          )}
          {page === 'profil' && (
            <ProfilPage dark={dark} density={density} setPage={handleSetPage} />
          )}
          {page === 'einstellungen' && (
            <EinstellungenPage
              dark={dark}
              setDark={v => setDark(v)}
              density={density}
              setDensity={setDensity}
            />
          )}
          {page === 'admin_einstellungen' && (
            <AdminEinstellungenPage dark={dark} density={density} />
          )}
        </main>
      </div>
    </div>
  );
}
